// AI Service for Claude Anthropic API Integration - CRM Version
// Handles all AI-related operations including true SSE streaming

// API_BASE_URL — mirrors the resolution logic in pie-sales-crm.tsx so every
// service file resolves the same backend URL regardless of environment.
//   • Production: VITE_API_PRODUCTION_PATH  (e.g. /dev-pieragsalesconnect/api)
//   • Development: VITE_API_BASE_URL or '/api' (relative, works with Vite proxy)
//   • Mixed-content guard: when page is HTTPS but env var points to HTTP, fall
//     back to '/api' (relative) so the browser doesn't block the request.
const _envApiUrl = import.meta.env.MODE === 'production'
  ? import.meta.env.VITE_API_PRODUCTION_PATH || '/dev-pieragsalesconnect/api'
  : import.meta.env.VITE_API_BASE_URL || '/api';

const API_BASE_URL = (typeof window !== 'undefined' && window.location.protocol === 'https:' && _envApiUrl?.startsWith('http://'))
  ? '/api'
  : _envApiUrl;

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

// Generate a unique session ID for conversation continuity
let sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

class AIService {
  /**
   * Stream a chat response via SSE (Server-Sent Events)
   * Text starts appearing within ~500ms instead of waiting for full response
   */
  static async chatStream(
    messages: Message[],
    onChunk: (chunk: string, fullResponse: string) => void,
    onAction?: (actionProposal: any) => void
  ): Promise<string> {
    try {
      const lastUserMessage = messages.filter(m => m.role === 'user').pop();
      if (!lastUserMessage) throw new Error('No user message found');

      const crmContext = (window as any).__crmContext || {};

      const response = await fetch(`${API_BASE_URL}/ai/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: lastUserMessage.content,
          context: crmContext,
          sessionId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try { errorData = JSON.parse(errorText); } catch { errorData = { message: errorText }; }
        throw new Error(errorData.message || `HTTP ${response.status}: ${errorText}`);
      }

      // Check if we got an SSE stream or a JSON error
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('text/event-stream')) {
        // Fallback: got JSON response instead of stream (e.g., error before headers flushed)
        const result = await response.json();
        const text = result.data?.response || result.message || 'No response';
        onChunk(text, text);
        return text;
      }

      // Parse SSE stream
      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete lines
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;

          try {
            const data = JSON.parse(line.slice(6));

            if (data.type === 'text') {
              accumulated += data.text;
              onChunk(data.text, accumulated);
            } else if (data.type === 'action' && onAction) {
              onAction(data.actionProposal);
            } else if (data.type === 'error') {
              throw new Error(data.message || 'AI streaming error');
            }
            // 'done' type is handled implicitly when stream ends
          } catch (parseErr: any) {
            // Skip malformed SSE lines but throw real errors
            if (parseErr.message && !parseErr.message.includes('JSON')) {
              throw parseErr;
            }
          }
        }
      }

      return accumulated;

    } catch (error: any) {
      if (error.message?.includes('API key') || error.message?.includes('authentication')) {
        throw new Error('AI service authentication failed. Please check server configuration.');
      }
      if (error.message?.includes('rate limit') || error.message?.includes('429')) {
        throw new Error('Rate limit exceeded. Please wait a moment and try again.');
      }
      if (error.message?.includes('fetch') || error.message?.includes('NetworkError') || error.message?.includes('Failed to fetch')) {
        throw new Error('Network error. Please check your connection and ensure the backend server is running.');
      }
      throw new Error(`AI request failed: ${error.message || 'Unknown error'}`);
    }
  }

  /**
   * Non-streaming chat (backward compatibility / fallback)
   */
  static async chat(messages: Message[], onChunk: ((chunk: string, fullResponse: string) => void) | null = null): Promise<string> {
    try {
      const lastUserMessage = messages.filter(m => m.role === 'user').pop();
      if (!lastUserMessage) throw new Error('No user message found');

      const crmContext = (window as any).__crmContext || {};

      const response = await fetch(`${API_BASE_URL}/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: lastUserMessage.content,
          context: crmContext,
          sessionId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try { errorData = JSON.parse(errorText); } catch { errorData = { message: errorText }; }
        throw new Error(errorData.message || `HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      const aiResponse = result.data?.response || result.data?.content || 'No response generated';

      // Deliver full response at once (no simulated streaming)
      if (onChunk && aiResponse) {
        onChunk(aiResponse, aiResponse);
      }

      return aiResponse;

    } catch (error: any) {
      if (error.message?.includes('API key') || error.message?.includes('authentication')) {
        throw new Error('AI service authentication failed. Please check server configuration.');
      }
      if (error.message?.includes('rate limit') || error.message?.includes('429')) {
        throw new Error('Rate limit exceeded. Please wait a moment and try again.');
      }
      if (error.message?.includes('fetch') || error.message?.includes('NetworkError') || error.message?.includes('Failed to fetch')) {
        throw new Error('Network error. Please check your connection and ensure the backend server is running.');
      }
      throw new Error(`AI request failed: ${error.message || 'Unknown error'}`);
    }
  }

  /**
   * Create a user message with CRM context
   */
  static createMessageWithContext(userQuery: string, context: any): Message {
    const backendContext = this.prepareContextForBackend(context);
    (window as any).__crmContext = backendContext;

    return {
      role: 'user',
      content: userQuery
    };
  }

  /**
   * Prepare context in the compact format expected by the backend.
   * Sends aggregated summaries + top 5 attention deals (not full record arrays).
   */
  static prepareContextForBackend(context: any): any {
    if (context.currentUser) {
      (window as any).__currentUser = context.currentUser;
    }

    // The new compact format from prepareCRMContext is already backend-ready.
    // Just pass it through — summary, attentionDeals, leads/customers/tasks as counts.
    return {
      summary: context.summary || {},
      attentionDeals: context.attentionDeals || [],
      allDeals: context.allDeals || [],
      leads: context.leads || { total: 0, byStatus: {}, bySource: {} },
      customers: context.customers || { total: 0, active: 0, avgHealthScore: 0 },
      tasks: context.tasks || { total: 0, open: 0, overdue: 0, topOverdue: [] },
      currentUser: context.currentUser || null
    };
  }

  /**
   * Test if the AI service is configured correctly
   */
  static async testConnection(): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/ai/health`, {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) return false;
      const result = await response.json();
      return result.success && result.configured;
    } catch (error) {
      console.error('AI Connection Test Failed:', error);
      return false;
    }
  }

  /**
   * Clear conversation history
   */
  static async clearConversation(): Promise<void> {
    try {
      await fetch(`${API_BASE_URL}/ai/conversation`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ sessionId })
      });

      // Generate new session ID
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    } catch (error) {
      console.error('Failed to clear conversation:', error);
    }
  }
}

export default AIService;
